/**
 * REST client for Cruise Itineraries API.
 * Mirrors WandererTrip iOS CruiseItinerariesAPI. See backend/docs/cruise-itineraries-ios.md.
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';

export interface CruiseLineVariant {
  subBrand?: string | null;
  cruiseType?: string | null;
}

export interface CruiseLineCatalogEntry {
  cruiseLine: string;
  variants?: CruiseLineVariant[] | null;
}

export interface CruiseEmbarkationRow {
  itineraryId: string;
  cruiseLine: string;
  subBrand?: string | null;
  cruiseType?: string | null;
  shipName: string;
  date: string;
  time: string;
  port: string;
  endDate?: string | null;
  endPort?: string | null;
  maxPassengers: number;
  crew: number;
  latitude?: number | null;
  longitude?: number | null;
  endLatitude?: number | null;
  endLongitude?: number | null;
  timeZoneIdentifier?: string | null;
  endTimeZoneIdentifier?: string | null;
}

export interface CruisePortStop {
  cruiseLine: string;
  subBrand?: string | null;
  cruiseType?: string | null;
  shipName: string;
  date: string;
  time: string;
  port: string;
  maxPassengers: number;
  crew: number;
  latitude?: number | null;
  longitude?: number | null;
  timeZoneIdentifier?: string | null;
}

export interface CruiseItineraryDetail {
  itineraryId: string;
  cruiseLine: string;
  subBrand?: string | null;
  cruiseType?: string | null;
  shipName: string;
  embarkDate: string;
  endDate: string;
  ports?: CruisePortStop[] | null;
}

export interface CruiseSailingDisplaySummary {
  embarkDate: string;
  debarkDate: string;
  embarkPort: string;
  debarkPort: string;
}

function itinerariesBase(): string {
  return `${API_BASE_URL}/itineraries`;
}

async function cruiseGet<T>(url: string): Promise<{ data: T; nextCursor: string | null }> {
  const headers = await buildAuthHeaders();
  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) {
    let body = '';
    try {
      body = await response.text();
    } catch {
      /* ignore */
    }
    throw new Error(body || `Cruise catalog request failed (${response.status}).`);
  }
  const next =
    response.headers.get('Next-Cursor')?.trim() ||
    response.headers.get('next-cursor')?.trim() ||
    null;
  const data = (await response.json()) as T;
  return { data, nextCursor: next || null };
}

export function normalizedCatalogField(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

export function variantLabel(variant: CruiseLineVariant): string | null {
  return normalizedCatalogField(variant.subBrand) ?? normalizedCatalogField(variant.cruiseType);
}

export function distinctSubBrands(entry: CruiseLineCatalogEntry): string[] {
  const brands = (entry.variants ?? [])
    .map(variantLabel)
    .filter((v): v is string => v != null);
  return Array.from(new Set(brands)).sort((a, b) => a.localeCompare(b));
}

function parseCruiseLines(body: unknown): CruiseLineCatalogEntry[] {
  if (!body || typeof body !== 'object') return [];
  const cruiseLines = (body as { cruiseLines?: unknown }).cruiseLines;
  if (!Array.isArray(cruiseLines)) return [];
  if (cruiseLines.length === 0) return [];
  if (typeof cruiseLines[0] === 'string') {
    return (cruiseLines as string[]).map((name) => ({ cruiseLine: name, variants: null }));
  }
  return cruiseLines as CruiseLineCatalogEntry[];
}

export async function listCruiseLines(): Promise<CruiseLineCatalogEntry[]> {
  const { data } = await cruiseGet<unknown>(`${itinerariesBase()}/cruise-lines`);
  return parseCruiseLines(data);
}

export async function listShipsByCruiseLine(cruiseLine: string): Promise<string[]> {
  const trimmed = cruiseLine.trim();
  if (!trimmed) throw new Error('Cruise line is required.');
  const { data } = await cruiseGet<{ ships?: string[] }>(
    `${itinerariesBase()}/cruise-lines/${encodeURIComponent(trimmed)}/ships`
  );
  return data.ships ?? [];
}

export async function listItineraries(params: {
  cruiseLine: string;
  date?: string;
  shipName?: string;
  cursor?: string;
  limit?: number;
}): Promise<{ cruises: CruiseEmbarkationRow[]; nextCursor: string | null }> {
  const trimmedLine = params.cruiseLine.trim();
  if (!trimmedLine) throw new Error('Cruise line is required.');
  const hasDate = Boolean(params.date?.trim());
  const hasShip = Boolean(params.shipName?.trim());
  if (!hasDate && !hasShip) {
    throw new Error('Select a ship or date to search sailings.');
  }

  const query = new URLSearchParams();
  query.set('cruiseLine', trimmedLine);
  if (hasDate && params.date) query.set('date', params.date);
  if (hasShip && params.shipName) query.set('shipName', params.shipName);
  if (params.cursor) query.set('cursor', params.cursor);
  query.set('limit', String(params.limit ?? 1000));

  const { data, nextCursor } = await cruiseGet<{ cruises?: CruiseEmbarkationRow[] }>(
    `${itinerariesBase()}?${query.toString()}`
  );
  return { cruises: data.cruises ?? [], nextCursor };
}

/** Fetch sailings for a cruise line for one month (YYYY-MM) or sample upcoming months. */
export async function listAllItinerariesForLine(
  cruiseLine: string,
  monthKey?: string | null,
  monthCount = 25
): Promise<CruiseEmbarkationRow[]> {
  const trimmed = cruiseLine.trim();
  if (!trimmed) throw new Error('Cruise line is required.');

  const dateStrings: string[] = [];
  if (monthKey) {
    dateStrings.push(`${monthKey}-01`);
  } else {
    const now = new Date();
    for (let offset = 0; offset < monthCount; offset += 1) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      dateStrings.push(`${y}-${m}-01`);
    }
  }

  const byId = new Map<string, CruiseEmbarkationRow>();
  for (const dateStr of dateStrings) {
    const { cruises } = await listItineraries({
      cruiseLine: trimmed,
      date: dateStr,
      limit: 1000,
    });
    for (const row of cruises) {
      byId.set(row.itineraryId, row);
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export async function getItinerary(itineraryId: string): Promise<CruiseItineraryDetail> {
  const id = itineraryId.trim();
  const { data } = await cruiseGet<CruiseItineraryDetail>(
    `${itinerariesBase()}/${encodeURIComponent(id)}`
  );
  return data;
}

export function displayPortName(raw: string): string {
  const trimmed = raw.trim();
  const base = trimmed.includes(',')
    ? trimmed.slice(0, trimmed.indexOf(',')).trim()
    : trimmed;
  return canonicalCatalogPortTitle(base) ?? base;
}

export function canonicalCatalogPortTitle(name: string): string | null {
  switch (name.toLowerCase().trim()) {
    case 'at sea':
    case 'atsea':
      return 'At Sea';
    case 'sea day':
      return 'Sea Day';
    case 'sea cruising':
      return 'Sea Cruising';
    case 'en route':
      return 'En Route';
    default:
      return null;
  }
}

export function isAtSeaLocation(name: string): boolean {
  const n = name.toLowerCase().trim();
  return (
    n === 'at sea' ||
    n === 'atsea' ||
    n === 'sea day' ||
    n === 'sea cruising' ||
    n === 'en route'
  );
}

/**
 * Skip country/ISO2 enrichment — mirrors iOS GeocodingService
 * isAtSeaLocation || isCruisingSegment (cruising / hotel overnight / flight).
 */
export function shouldSkipCountryEnrichment(name: string): boolean {
  const n = name.toLowerCase().trim();
  if (isAtSeaLocation(name)) return true;
  if (n.includes('cruising')) return true;
  if (n.includes('flight')) return true;
  if (n.includes('hotel') && n.includes('overnight')) return true;
  return false;
}

export type PortStopIconKind = 'dockedPort' | 'atSeaOrCruising' | 'flight' | 'hotel';

/** Matches iOS GeocodingService.portStopIconKind */
export function portStopIconKind(cityName: string): PortStopIconKind {
  const n = cityName.toLowerCase().trim();
  if (n.includes('hotel') || n.includes('overnight')) return 'hotel';
  if (n.includes('flight')) return 'flight';
  if (isAtSeaLocation(cityName) || n.includes('cruising')) return 'atSeaOrCruising';
  return 'dockedPort';
}

/**
 * Parses API `time` (`13:00` or `13:00-20:00`) into HH:mm parts.
 * Mirrors iOS CruiseItineraryMapper.parsePortTimes.
 */
export function parsePortTimeParts(time: string): {
  arrivalHm: string | null;
  departureHm: string | null;
} {
  const trimmed = time.trim();
  if (!trimmed) return { arrivalHm: null, departureHm: null };
  if (trimmed.includes('-')) {
    const parts = trimmed.split('-').map((p) => p.trim());
    if (parts.length >= 2) {
      return {
        arrivalHm: normalizeHm(parts[0]),
        departureHm: normalizeHm(parts[1]),
      };
    }
  }
  return { arrivalHm: null, departureHm: normalizeHm(trimmed) };
}

function normalizeHm(raw: string): string | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(raw.trim());
  if (!match) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

function formatPortClock(hm: string): string {
  const [h, m] = hm.split(':').map(Number);
  const date = new Date(Date.UTC(2000, 0, 1, h, m));
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** e.g. "Mon, Sep 7" — matches iOS weekday.abbreviated + day + month */
export function formatPortDisplayDate(yyyyMMdd: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMMdd.trim());
  if (!match) return yyyyMMdd;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export interface CruisePortPreviewRow {
  key: string;
  name: string;
  dateLabel: string;
  timeLine: string | null;
  icon: PortStopIconKind;
}

/**
 * Preview rows for the Ports accordion — mirrors iOS CruisePortStopPreviewRow.
 */
export function cruisePortPreviewRows(
  ports: CruisePortStop[] | null | undefined,
  copy: {
    timeRange: (arrival: string, departure: string) => string;
    arrivalOnly: (time: string) => string;
    departureOnly: (time: string) => string;
  }
): CruisePortPreviewRow[] {
  if (!ports?.length) return [];
  return ports.map((stop, index) => {
    const name = displayPortName(stop.port);
    const { arrivalHm, departureHm } = parsePortTimeParts(stop.time ?? '');
    let timeLine: string | null = null;

    const isFullDayDefault =
      arrivalHm === '00:00' &&
      (departureHm === '23:59' || departureHm === '23:59:00');

    if (arrivalHm && departureHm && !isFullDayDefault) {
      timeLine = copy.timeRange(formatPortClock(arrivalHm), formatPortClock(departureHm));
    } else if (arrivalHm && !departureHm) {
      timeLine = copy.arrivalOnly(formatPortClock(arrivalHm));
    } else if (departureHm && !arrivalHm) {
      timeLine = copy.departureOnly(formatPortClock(departureHm));
    }

    return {
      key: `${stop.date}-${name}-${index}`,
      name,
      dateLabel: formatPortDisplayDate(stop.date),
      timeLine,
      icon: portStopIconKind(name),
    };
  });
}

export function sailingDisplaySummaryFromRow(row: CruiseEmbarkationRow): CruiseSailingDisplaySummary {
  const debarkDate = row.endDate?.trim() || '';
  const debarkPort = row.endPort?.trim() || '';
  return {
    embarkDate: row.date,
    debarkDate: debarkDate || row.date,
    embarkPort: displayPortName(row.port),
    debarkPort: debarkPort ? displayPortName(debarkPort) : displayPortName(row.port),
  };
}

export function sailingDisplaySummaryFromDetail(
  detail: CruiseItineraryDetail
): CruiseSailingDisplaySummary {
  const ports = detail.ports ?? [];
  if (ports.length > 0) {
    const first = ports[0];
    const last = ports[ports.length - 1];
    return {
      embarkDate: first.date || detail.embarkDate,
      debarkDate: last.date || detail.endDate,
      embarkPort: displayPortName(first.port),
      debarkPort: displayPortName(last.port),
    };
  }
  return {
    embarkDate: detail.embarkDate,
    debarkDate: detail.endDate,
    embarkPort: '',
    debarkPort: '',
  };
}

export function formatEmbarkDateMedium(yyyyMMdd: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMMdd.trim());
  if (!match) return yyyyMMdd;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatEmbarkDateShort(yyyyMMdd: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMMdd.trim());
  if (!match) return yyyyMMdd;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Matches iOS ImportCruiseItineraryView.SailingRowFormatting.portsAreSame */
export function portsAreSame(embarkPort: string, debarkPort: string): boolean {
  return embarkPort.trim().toLowerCase() === debarkPort.trim().toLowerCase();
}

/** Matches iOS dateRangeLine — e.g. "Oct 3 - Nov 6" */
export function sailingDateRangeLine(summary: CruiseSailingDisplaySummary): string {
  const embark = formatEmbarkDateShort(summary.embarkDate);
  const debarkDate = summary.debarkDate.trim();
  if (!debarkDate || debarkDate === summary.embarkDate) return embark;
  return `${embark} - ${formatEmbarkDateShort(debarkDate)}`;
}

/** Matches iOS portRouteLine — e.g. "Hong Kong to Sydney" / "Piraeus round-trip" */
export function sailingPortRouteLine(
  summary: CruiseSailingDisplaySummary,
  copy: {
    roundTrip: (port: string) => string;
    portToPort: (from: string, to: string) => string;
  }
): string {
  const embarkPort = summary.embarkPort.trim();
  const debarkPort = summary.debarkPort.trim();
  if (!embarkPort) return '';
  if (!debarkPort || portsAreSame(embarkPort, debarkPort)) {
    return copy.roundTrip(embarkPort);
  }
  return copy.portToPort(embarkPort, debarkPort);
}

/** Matches iOS dateAndPortLine */
export function sailingDateAndPortLine(
  summary: CruiseSailingDisplaySummary,
  copy: {
    roundTrip: (port: string) => string;
    portToPort: (from: string, to: string) => string;
  }
): string {
  const ports = sailingPortRouteLine(summary, copy);
  const dates = sailingDateRangeLine(summary);
  if (!ports) return dates;
  return `${dates} ${ports}`;
}

/**
 * Matches iOS shipLine — e.g. "Azamara Pursuit · 4840494".
 * Itinerary id shown to match the native DEBUG sailings list the product compares against.
 */
export function sailingShipLine(
  row: CruiseEmbarkationRow,
  options: { showItineraryId?: boolean } = { showItineraryId: true }
): string {
  const parts: string[] = [];
  const subBrand = normalizedCatalogField(row.subBrand);
  if (subBrand) parts.push(subBrand);
  const ship = row.shipName.trim();
  if (ship) parts.push(ship);

  if (!options.showItineraryId) {
    return parts.join(' · ');
  }

  const itineraryId = row.itineraryId.trim();
  if (parts.length === 0) return itineraryId;
  if (!itineraryId) return parts.join(' · ');
  return `${parts.join(' · ')} · ${itineraryId}`;
}

export function defaultTripName(detail: CruiseItineraryDetail): string {
  return `${detail.shipName} — ${formatEmbarkDateMedium(detail.embarkDate)}`;
}

export function addDaysToIsoDate(isoDate: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!match) return isoDate;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + days);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Whole calendar days from `fromIso` to `toIso` (UTC date-only). */
export function diffIsoCalendarDays(fromIso: string, toIso: string): number {
  const fromMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fromIso.trim());
  const toMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(toIso.trim());
  if (!fromMatch || !toMatch) return 0;
  const from = Date.UTC(Number(fromMatch[1]), Number(fromMatch[2]) - 1, Number(fromMatch[3]));
  const to = Date.UTC(Number(toMatch[1]), Number(toMatch[2]) - 1, Number(toMatch[3]));
  return Math.round((to - from) / 86_400_000);
}

export function monthsByYear(
  monthCount = 25,
  startDate = new Date()
): { year: number; months: { key: string; label: string }[] }[] {
  const result: { year: number; months: { key: string; label: string }[] }[] = [];
  let year = -1;
  let months: { key: string; label: string }[] = [];
  const start = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 1));

  for (let i = 0; i < monthCount; i += 1) {
    const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + i, 1));
    const y = d.getUTCFullYear();
    const key = `${y}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
    if (y !== year) {
      if (months.length > 0) result.push({ year, months });
      year = y;
      months = [];
    }
    months.push({ key, label });
  }
  if (months.length > 0) result.push({ year, months });
  return result;
}

export function monthSummaryLabel(monthKey: string | null): string {
  if (!monthKey) return 'All dates';
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!match) return monthKey;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function filterSailings(
  sailings: CruiseEmbarkationRow[],
  includedShipFilters: Set<string>,
  includedSubBrandFilters: Set<string>
): CruiseEmbarkationRow[] {
  let rows = [...sailings].sort((a, b) => a.date.localeCompare(b.date));
  if (includedShipFilters.size > 0) {
    rows = rows.filter((r) => includedShipFilters.has(r.shipName));
  }
  if (includedSubBrandFilters.size > 0) {
    rows = rows.filter((row) => {
      const label = normalizedCatalogField(row.subBrand) ?? normalizedCatalogField(row.cruiseType);
      return label != null && includedSubBrandFilters.has(label);
    });
  }
  return rows;
}

export function groupSailingsByMonth(
  sailings: CruiseEmbarkationRow[]
): { monthKey: string; header: string; rows: CruiseEmbarkationRow[] }[] {
  const groups = new Map<string, CruiseEmbarkationRow[]>();
  for (const row of [...sailings].sort((a, b) => a.date.localeCompare(b.date))) {
    const key = row.date.slice(0, 7);
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  }
  return Array.from(groups.keys())
    .sort()
    .map((monthKey) => {
      const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
      let header = monthKey;
      if (match) {
        const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
        header = date.toLocaleDateString(undefined, {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        });
      }
      return { monthKey, header, rows: groups.get(monthKey) ?? [] };
    });
}
